import { questionTextToPlainText } from 'utils/questionMarkdown';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useToast } from 'components/ui/use-toast';
import { useAuth } from 'contexts/AuthContext';
import { questions as questionsApi, user as userApi } from 'services/api';
import type { Question } from '../../types';
import { hasQuestionGeneration } from 'utils/questionGeneration';
import { hasAnswerReferences } from './useAnswerReferences';

interface UseQuestionNarrationParams {
  question: Question | null;
  upcomingQuestionIds?: number[];
}

interface UseQuestionNarrationResult {
  isAvailable: boolean;
  isEnabled: boolean;
  isPlaying: boolean;
  isLoading: boolean;
  isSavingSetting: boolean;
  canNarrateCurrentQuestion: boolean;
  toggleEnabled: (nextEnabled: boolean) => Promise<void>;
  replay: () => Promise<void>;
  stop: () => void;
}

const getNarrationQuestionType = (question: Question | null): string => {
  return String(question?.question_type_display ?? question?.question_type ?? '').trim().toLowerCase();
};

const buildNarrationPreviewText = (question: Question | null): string => {
  if (!question) {
    return '';
  }
  const fields = [question.header, question.text, question.subtext,
    ...(['rating', 'boolean', 'styled_boolean', 'true_false'].includes(getNarrationQuestionType(question))
      ? [question.low_text, question.high_text] : [])];
  return fields
    .map((value) => questionTextToPlainText(value).trim())
    .filter(Boolean)
    .join('\n\n');
};

const isAutoplayBlockedError = (error: unknown): boolean => {
  if (!(error instanceof Error)) {
    return false;
  }

  const message = error.message.toLowerCase();
  return (
    error.name === 'NotAllowedError' ||
    message.includes('notallowederror') ||
    message.includes('user gesture') ||
    message.includes('play() failed')
  );
};

export const useQuestionNarration = ({
  question,
  upcomingQuestionIds = [],
}: UseQuestionNarrationParams): UseQuestionNarrationResult => {
  const { addToast } = useToast();
  const { user, updateUser } = useAuth();
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSavingSetting, setIsSavingSetting] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const playbackRequestIdRef = useRef(0);

  const currentSettings = useMemo(() => user?.settings ?? {}, [user?.settings]);
  const isAvailable = Boolean(currentSettings.question_flow_tts_available);
  const isEnabled = Boolean(currentSettings.question_flow_tts_enabled);
  const questionId = typeof question?.id === 'number' ? question.id : null;
  const narrationText = useMemo(() => buildNarrationPreviewText(question), [question]);
  const questionType = useMemo(() => getNarrationQuestionType(question), [question]);
  const normalizedUpcomingQuestionIds = useMemo(
    () => upcomingQuestionIds.filter((id) => typeof id === 'number').slice(0, 2),
    [upcomingQuestionIds],
  );

  const canNarrateCurrentQuestion =
    Boolean(questionId) &&
    !hasQuestionGeneration(question) &&
    !hasAnswerReferences(question) &&
    narrationText.length > 0 &&
    questionType !== 'custom_screen';

  const stop = useCallback(() => {
    playbackRequestIdRef.current += 1;
    const audio = audioRef.current;
    audioRef.current = null;
    if (audio) {
      audio.pause();
      audio.src = '';
    }
    setIsPlaying(false);
    setIsLoading(false);
  }, []);

  const replay = useCallback(async () => {
    if (!isAvailable || !canNarrateCurrentQuestion || !questionId) {
      return;
    }

    stop();
    const requestId = playbackRequestIdRef.current + 1;
    playbackRequestIdRef.current = requestId;
    setIsLoading(true);

    try {
      const narration = await questionsApi.getNarration(questionId, normalizedUpcomingQuestionIds);
      if (playbackRequestIdRef.current !== requestId) {
        return;
      }

      const audio = new Audio(narration.audio_url);
      audioRef.current = audio;
      audio.onended = () => {
        if (audioRef.current === audio) {
          audioRef.current = null;
        }
        setIsPlaying(false);
        setIsLoading(false);
      };
      audio.onerror = () => {
        if (audioRef.current === audio) {
          audioRef.current = null;
        }
        setIsPlaying(false);
        setIsLoading(false);
      };

      await audio.play();
      if (playbackRequestIdRef.current !== requestId) {
        audio.pause();
        return;
      }
      setIsPlaying(true);
    } catch (error) {
      if (playbackRequestIdRef.current === requestId) {
        setIsPlaying(false);
        setIsLoading(false);
        if (!isAutoplayBlockedError(error)) {
          addToast({
            title: 'Narration unavailable',
            description: 'Unable to play question narration right now.',
            variant: 'destructive',
          });
        }
      }
    } finally {
      if (playbackRequestIdRef.current === requestId) {
        setIsLoading(false);
      }
    }
  }, [addToast, canNarrateCurrentQuestion, isAvailable, normalizedUpcomingQuestionIds, questionId, stop]);

  const toggleEnabled = useCallback(
    async (nextEnabled: boolean) => {
      if (!isAvailable || isSavingSetting) {
        return;
      }

      const previousSettings = {
        ...currentSettings,
        question_flow_tts_enabled: isEnabled,
      };
      const optimisticSettings = {
        ...currentSettings,
        question_flow_tts_enabled: nextEnabled,
      };

      updateUser({ settings: optimisticSettings });
      if (!nextEnabled) {
        stop();
      }

      setIsSavingSetting(true);
      try {
        const updatedProfile = await userApi.updateProfile({
          settings: { question_flow_tts_enabled: nextEnabled },
        });
        updateUser({
          settings: {
            ...currentSettings,
            question_flow_tts_enabled: Boolean(updatedProfile?.settings?.question_flow_tts_enabled),
            question_flow_tts_available: Boolean(updatedProfile?.settings?.question_flow_tts_available),
          },
        });
      } catch (error) {
        updateUser({ settings: previousSettings });
        if (!previousSettings.question_flow_tts_enabled) {
          stop();
        }
        addToast({
          title: 'Setting not saved',
          description: 'Unable to update question narration right now.',
          variant: 'destructive',
        });
      } finally {
        setIsSavingSetting(false);
      }
    },
    [addToast, currentSettings, isAvailable, isEnabled, isSavingSetting, stop, updateUser],
  );

  useEffect(() => {
    if (!isAvailable || !isEnabled || !canNarrateCurrentQuestion) {
      stop();
      return;
    }

    void replay();
    return () => {
      stop();
    };
  }, [canNarrateCurrentQuestion, isAvailable, isEnabled, questionId, replay, stop]);

  useEffect(() => {
    return () => {
      stop();
    };
  }, [stop]);

  return {
    isAvailable,
    isEnabled,
    isPlaying,
    isLoading,
    isSavingSetting,
    canNarrateCurrentQuestion,
    toggleEnabled,
    replay,
    stop,
  };
};
